/**
 * Legacy wire spellings → the canonical wire shape.
 *
 * Masks written before `kind`/`polygon` existed spell the kind `mask_type` and carry a rect
 * `geometry` instead of a polygon. Those records are still in the catalog — the cloud accepts
 * BOTH spellings on input (`cloud/app/catalog/schemas.py::MaskShape._accept_legacy_spelling`)
 * but it never rewrites what is already stored, so an un-migrated version comes back exactly
 * as it was written.
 *
 * This module is the ONE normaliser for that spelling, and BOTH seams use it:
 *   - WRITE — `@genz/platform`'s `jsonClient.patchVersionPayload` normalises what it re-sends
 *     (a Templates → Activate that re-posts what it read used to 422; AUD-6 LB-1);
 *   - READ  — `adapter.ts::wireViewToEngine` normalises what it hands the engine (F-4: an
 *     un-normalised mask crossed with `kind: undefined` and serde killed the batch with
 *     "invalid type: unit value, expected enum MaskKind").
 *
 * It lives here, not in `@genz/platform`, because the dependency runs platform → engine-bind
 * and the canonical wire types (`wire.ts`) live here. Two copies is how the two seams drifted.
 */

/** Normalized corner polygon for a legacy `{kind:"rect", x, y, w, h}` geometry; `null` for
 *  anything else (already-polygonal records, unknown kinds, junk) so the caller leaves the
 *  existing `polygon` alone. Mirrors the cloud's `_polygon_from_geometry`. */
function polygonFromGeometry(geometry: unknown): number[][] | null {
  if (!geometry || typeof geometry !== "object" || Array.isArray(geometry)) return null;
  const g = geometry as Record<string, unknown>;
  if (g.kind !== "rect") return null;
  const [x, y, w, h] = [g.x, g.y, g.w, g.h].map(Number);
  if (![x, y, w, h].every((n) => Number.isFinite(n))) return null;
  return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
}

/**
 * Normalize a view's `masks` to the canonical `{kind, polygon}` wire shape.
 *
 * An existing `kind`/`polygon` ALWAYS wins — this only fills in what the legacy spelling
 * expresses differently. Legacy keys are left in place (harmless: the server ignores them and
 * the engine adapter reads only the canonical ones). Non-array input passes through unchanged
 * so a partially-synced view cannot turn into a crash.
 */
export function normalizeMasksForWire(masks: unknown): unknown {
  if (!Array.isArray(masks)) return masks;
  return masks.map((m) => {
    if (!m || typeof m !== "object" || Array.isArray(m)) return m;
    const mask = m as Record<string, unknown>;
    const out: Record<string, unknown> = { ...mask };
    if (!out.kind && typeof mask.mask_type === "string") out.kind = mask.mask_type;
    const poly = out.polygon;
    if (!Array.isArray(poly) || poly.length === 0) {
      const fromGeometry = polygonFromGeometry(mask.geometry);
      if (fromGeometry) out.polygon = fromGeometry;
    }
    return out;
  });
}
